import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // Update "VISA ALLA" live while typing (href + visibility)
    this.refs.searchInput.addEventListener(
      'input',
      () => {
        this.#updateViewAllButton();
      },
      { signal }
    );

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /** @type {boolean} */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => item.classList.remove('keyboard-focus'));

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1
          : currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) return;

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');
      onAnimationEnd(recentlyViewedWrapper, () => recentlyViewedWrapper.remove());
    }
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) resultsInner.scrollTop = 0;

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) formContent.scrollTop = 0;
    });
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = abortController;
    return abortController;
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  // --- VIEW ALL BUTTON (inject + update) ---
  #ensureViewAllButton() {
    const root = this.refs.predictiveSearchResults;
    if (!root) return null;

    let wrap = root.querySelector('[data-predictive-view-all]');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.setAttribute('data-predictive-view-all', '');
      wrap.innerHTML = `
        <a class="header-search-inline__view-all-btn" href="${Theme.routes.search_url}?q=">
          VISA ALLA
        </a>
      `;
      root.appendChild(wrap);
    }

    const a = wrap.querySelector('a');
    return a instanceof HTMLAnchorElement ? a : null;
  }

  #updateViewAllButton() {
    const root = this.refs.predictiveSearchResults;
    const a = this.#ensureViewAllButton();
    if (!root || !a) return;

    const qRaw = (this.refs.searchInput?.value || '').trim();

    // Hide if no query OR no product cards showing
    const shownProducts =
      root.querySelectorAll('.predictive-search-results__wrapper-products .predictive-search-results__card').length;

    const wrap = root.querySelector('[data-predictive-view-all]');
    if (!(wrap instanceof HTMLElement)) return;

    if (!qRaw || shownProducts === 0) {
      wrap.hidden = true;
      return;
    }

    const href = new URL(Theme.routes.search_url, location.origin);
    href.searchParams.set('q', qRaw);
    a.href = href.toString();

    // Always without count
    a.textContent = 'VISA ALLA';

    wrap.hidden = false;
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const raw = (searchTerm || '').trim();
    if (!raw) return;

    const lower = raw.toLowerCase();

    // Remove common separators: space, hyphen, underscore, dot, slash
    const compact = lower.replace(/[\s\-_./]+/g, '');

    // Digits only (EAN / numeric SKU)
    const digitsOnly = lower.replace(/\D+/g, '');

    // Tokenize between letters/numbers + remove separators to spaces
    // "ms400c-mw" -> "ms 400 c mw"
    const tokenized = lower
      .replace(/[\s\-_./]+/g, ' ')
      .replace(/([a-zåäö])(\d)/gi, '$1 $2')
      .replace(/(\d)([a-zåäö])/gi, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    // Build variants set (OR-query)
    const variants = new Set();
    variants.add(raw);
    variants.add(lower);
    if (compact) variants.add(compact);
    if (tokenized) variants.add(tokenized);

    // Numeric heuristics:
    // 594965901 => also try 5949659-01 and 5949659 01
    if (digitsOnly.length >= 6) {
      variants.add(digitsOnly);

      if (digitsOnly.length >= 3) {
        variants.add(`${digitsOnly.slice(0, -2)}-${digitsOnly.slice(-2)}`);
        variants.add(`${digitsOnly.slice(0, -2)} ${digitsOnly.slice(-2)}`);
      }
    }

    // Prepare query parts (limit to keep it snappy)
    const queryParts = Array.from(variants)
      .map((v) => String(v).trim())
      .filter(Boolean)
      .slice(0, 6);

    // Quote "code-like" terms with special chars so '-' isn't treated as NOT
    const quoteIfNeeded = (s) => {
      const hasSpecial = /[-_./]/.test(s);
      const hasSpace = /\s/.test(s);
      if (hasSpecial && !hasSpace) return `"${s.replace(/"/g, '\\"')}"`;
      return s;
    };

    const q =
      queryParts.length > 1
        ? queryParts.map((p) => `(${quoteIfNeeded(p)})`).join(' OR ')
        : quoteIfNeeded(queryParts[0]);

    // --- HTML request (theme section rendering) ---
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', q);

    // Keep theme behavior
    url.searchParams.set('resources[limit_scope]', 'each');

    // IMPORTANT: Tell Shopify predictive search which fields to use
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,tag,vendor,variants.sku,variants.barcode'
    );
    url.searchParams.set('resources[options][prefix]', 'last');

    const abortController = this.#createAbortController();
    const { signal } = abortController;

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        if (signal.aborted) return;

        morph(this.refs.predictiveSearchResults, resultsMarkup);

        // Inject + update "VISA ALLA"
        this.#updateViewAllButton();

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (signal.aborted) return;
        throw error;
      });
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);

    // Ensure button exists but stays hidden on empty state
    this.#updateViewAllButton();

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
